import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";

const TONE = { delete: "bg-rose-600", inactivate: "bg-amber-600", review: "bg-slate-500" };
const LABEL = { delete: "Delete (unused)", inactivate: "Inactive + hidden", review: "Check by hand" };

const PlanGroup = ({ g }) => (
  <div className="border rounded p-2 text-xs" data-testid="cleanup-group">
    <div className="font-semibold">Keep: {g.keeper_name} <span className="font-mono text-slate-500">{g.keeper_id.slice(0, 8)}</span>
      {g.keeper_bank_complete ? <Badge className="ml-1 bg-emerald-600 text-[9px]">Bank complete</Badge> : <Badge variant="outline" className="ml-1 text-[9px]">No bank yet</Badge>}
      {g.relink && <Badge variant="secondary" className="ml-1 text-[9px]">Will link to login</Badge>}
    </div>
    {g.actions.map((a) => (
      <div key={a.payee_id} className="mt-1 flex items-center gap-2">
        <Badge className={`${TONE[a.action]} text-[9px]`}>{LABEL[a.action]}</Badge>
        <span className="font-mono">{a.payee_id.slice(0, 8)}</span>
        <span className="text-slate-500">{a.action === "review" ? a.note : `${a.moved_refs} pending claim(s) moved · ${a.locked_refs} approved/paid kept`}</span>
      </div>
    ))}
  </div>
);

export const PayeeCleanupButton = ({ onDone }) => {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (apply) => {
    setBusy(true);
    try {
      const r = await api.post("/v2/payees/duplicates-cleanup", { apply });
      if (apply) { toast.success("Cleanup applied"); setPlan(null); onDone(); } else setPlan(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); }
    setBusy(false);
  };
  const s = plan?.summary;
  return (
    <>
      <Button size="sm" onClick={() => run(false)} disabled={busy} data-testid="cleanup-dry-run-btn"><Wand2 className="w-3.5 h-3.5 mr-1" /> Clean up…</Button>
      {plan && (
        <Dialog open onOpenChange={(o) => { if (!o) setPlan(null); }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cleanup-dialog">
            <DialogHeader><DialogTitle>Cleanup preview (nothing changed yet)</DialogTitle></DialogHeader>
            <div className="text-sm" data-testid="cleanup-summary">
              {plan.groups.length} person group(s) · {s.delete} to delete · {s.inactivate} to mark inactive · {s.review} to check by hand · {s.moved_refs} pending claim(s) to move.
            </div>
            {plan.skipped_names_different_people.length > 0 && (
              <div className="text-xs text-slate-500">Left alone (same name, different logins): {plan.skipped_names_different_people.join(", ")}</div>
            )}
            <div className="space-y-2">{plan.groups.map((g) => <PlanGroup key={g.keeper_id} g={g} />)}</div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPlan(null)} data-testid="cleanup-cancel-btn">Cancel</Button>
              <Button onClick={() => run(true)} disabled={busy || plan.groups.length === 0} data-testid="cleanup-apply-btn">Confirm &amp; apply</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
